// Booster PWA bootstrap.
//
// Two responsibilities:
//   1. Register the service worker so the app is installable + can receive
//      push events even when no tab is open.
//   2. Expose window.boosterPush.{subscribe, unsubscribe, status} so a
//      Stimulus controller (or a plain inline button on /d/:slug/settings)
//      can flip the user's web-push state without re-implementing the
//      PushManager dance every time.

const SW_PATH = "/service-worker.js"

function vapidPublicKey() {
  const meta = document.querySelector('meta[name="vapid-public-key"]')
  return meta ? meta.content : null
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const raw = window.atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i)
  return out
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: "/" })
    return reg
  } catch (err) {
    console.warn("[booster/sw] registration failed:", err)
    return null
  }
}

async function currentSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

async function subscribe() {
  const key = vapidPublicKey()
  if (!key) throw new Error("VAPID public key not configured")
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications not supported")
  }

  const permission = await Notification.requestPermission()
  if (permission !== "granted") throw new Error("Notification permission denied")

  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key)
  })

  // Send to server. CSRF token from meta tag.
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content
  const subJson = sub.toJSON()
  const res = await fetch("/push/subscribe", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrf
    },
    body: JSON.stringify({
      endpoint:    subJson.endpoint,
      keys:        subJson.keys,
      userAgent:   navigator.userAgent,
      deviceLabel: deviceLabel()
    })
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `subscribe failed: ${res.status}`)
  }
  return sub
}

async function unsubscribe() {
  const sub = await currentSubscription()
  if (!sub) return false

  const csrf = document.querySelector('meta[name="csrf-token"]')?.content
  await fetch(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrf, "Accept": "application/json" }
  })
  await sub.unsubscribe()
  return true
}

function deviceLabel() {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua))  return "iPhone"
  if (/Android/.test(ua)) return "Android"
  if (/Macintosh/.test(ua)) return "Mac"
  if (/Windows/.test(ua))   return "Windows"
  return "Browser"
}

async function status() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { supported: false }
  }
  const sub = await currentSubscription()
  return {
    supported:  true,
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
    subscribed: !!sub,
    endpoint:   sub ? sub.endpoint : null
  }
}

// Register on every page load — registration is idempotent.
registerServiceWorker()

// Expose imperative API for inline buttons.
window.boosterPush = { subscribe, unsubscribe, status, currentSubscription }
